/**
 * Logging utility for TacticsBoard
 * Provides consistent logging with severity levels
 * and can be easily extended to integrate with external
 * logging services in production
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

// Set default log level based on environment
const DEFAULT_LOG_LEVEL = process.env.NODE_ENV === 'production' 
  ? LOG_LEVELS.ERROR 
  : LOG_LEVELS.DEBUG;

// Current logging configuration
let config = {
  level: DEFAULT_LOG_LEVEL,
  useColors: true,
  captureStack: true,
  externalLogger: null
};

/**
 * Configure the logger
 * @param {Object} options - Configuration options
 * @param {number} options.level - Minimum log level to display
 * @param {boolean} options.useColors - Whether to use colors in console output
 * @param {boolean} options.captureStack - Whether to capture stack traces
 * @param {Function} options.externalLogger - External logging service function
 */
export const configureLogger = (options = {}) => {
  config = { ...config, ...options };
};

/**
 * Get a timestamp string for logs
 * @returns {string} Formatted timestamp
 */
const getTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Format a log message with timestamp and context
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 * @returns {Object} Formatted log object
 */
const formatLog = (level, message, context = {}) => {
  const timestamp = getTimestamp();
  const stack = config.captureStack ? new Error().stack : undefined;
  
  return {
    timestamp,
    level,
    message,
    ...context,
    stack: stack ? stack.split('\n').slice(3).join('\n') : undefined
  };
};

/**
 * Send log to external logging service if configured
 * @param {Object} logData - Log data to send
 */
const sendToExternalLogger = (logData) => {
  if (config.externalLogger && typeof config.externalLogger === 'function') {
    try {
      config.externalLogger(logData);
    } catch (error) {
      // Don't let external logger errors affect application
      console.error('External logging error:', error);
    }
  }
};

/**
 * Debug level log
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
export const debug = (message, context = {}) => {
  if (config.level <= LOG_LEVELS.DEBUG) {
    const logData = formatLog('debug', message, context);
    
    if (config.useColors) {
      console.debug('%c[DEBUG] %c%s', 'color: gray', 'color: inherit', message, context);
    } else {
      console.debug('[DEBUG]', message, context);
    }
    
    sendToExternalLogger(logData);
  }
};

/**
 * Info level log
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
export const info = (message, context = {}) => {
  if (config.level <= LOG_LEVELS.INFO) {
    const logData = formatLog('info', message, context);
    
    if (config.useColors) {
      console.info('%c[INFO] %c%s', 'color: blue', 'color: inherit', message, context);
    } else {
      console.info('[INFO]', message, context);
    }
    
    sendToExternalLogger(logData);
  }
};

/**
 * Warning level log
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
export const warn = (message, context = {}) => {
  if (config.level <= LOG_LEVELS.WARN) {
    const logData = formatLog('warn', message, context);
    
    if (config.useColors) {
      console.warn('%c[WARN] %c%s', 'color: orange', 'color: inherit', message, context);
    } else {
      console.warn('[WARN]', message, context);
    }
    
    sendToExternalLogger(logData);
  }
};

/**
 * Error level log
 * @param {string} message - Error message
 * @param {Error|Object} error - Error object or context
 */
export const error = (message, error = {}) => {
  if (config.level <= LOG_LEVELS.ERROR) {
    const context = error instanceof Error 
      ? { errorName: error.name, errorMessage: error.message, stack: error.stack }
      : error;
    
    const logData = formatLog('error', message, context);
    
    if (config.useColors) {
      console.error('%c[ERROR] %c%s', 'color: red', 'color: inherit', message, error);
    } else {
      console.error('[ERROR]', message, error);
    }
    
    sendToExternalLogger(logData);
  }
};

/**
 * Group related logs
 * @param {string} label - Group label
 * @param {Function} groupFn - Function containing grouped logs
 */
export const group = (label, groupFn) => {
  if (typeof groupFn !== 'function') return;
  
  console.group(label);
  try {
    groupFn();
  } finally {
    console.groupEnd();
  }
};

/**
 * Log performance timing
 * @param {string} label - Label for the timing
 * @param {Function} fn - Function to time
 * @returns {*} Result of the function
 */
export const time = async (label, fn) => {
  if (config.level > LOG_LEVELS.DEBUG) {
    return fn();
  }
  
  const start = window.performance.now();
  try {
    return await fn();
  } finally {
    const duration = window.performance.now() - start;
    debug(`${label} took ${duration.toFixed(2)}ms`);
  }
};

// Export the logger as both individual functions and a combined object
const logger = {
  configureLogger,
  debug,
  info,
  warn,
  error,
  group,
  time,
  LOG_LEVELS
};

export default logger;
